import { requireCompletedProfile } from "@hisani/auth";
import { AppError } from "@hisani/core";
import { STABLE_ERROR_CODES, } from "../contracts.js";
import { getHireConversationSchema, listHireConversationsForStableSchema, sendHireMessageSchema, stableNotFound, } from "../validation.js";
import { requireFeatureMembership } from "./sealed-record-support.js";
import { hireConversationResponse, hireConversationSummaryResponse, hireMessageResponse, } from "./hire-response-projections.js";
const FEATURE = "HIRE";
/** Authorizes either side of a Hire conversation: the applicant themselves,
 * or a current member of the posting Stable with `HIRE` feature access
 * (`docs/products/stable.md`: "managing applicants is restricted to
 * Trainer, Co-Trainer, and Assistant Trainer") — never a fixed single
 * counterparty id, unlike Marketplace's buyer/seller pair, since the
 * "other side" here is the Stable itself. */
async function authorizeConversationAccess(dependencies, conversationId, actorId, level) {
    const participants = await dependencies.repository.getHireConversationParticipants(conversationId);
    if (participants === null)
        throw stableNotFound();
    if (participants.applicantHorsemanId === actorId)
        return "APPLICANT";
    await requireFeatureMembership(dependencies, participants.stableId, actorId, FEATURE, level);
    return "STABLE";
}
export async function getHireConversation(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getHireConversationSchema.parse(input);
    const viewerRole = await authorizeConversationAccess(dependencies, parsed.conversationId, actor.account.id, "VIEW");
    const conversation = await dependencies.repository.getHireConversation({
        conversationId: parsed.conversationId,
        cursor: parsed.cursor,
        limit: parsed.limit,
    });
    if (conversation === null)
        throw stableNotFound();
    return hireConversationResponse(conversation, viewerRole);
}
export async function sendHireMessage(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = sendHireMessageSchema.parse(input);
    const senderRole = await authorizeConversationAccess(dependencies, parsed.conversationId, actor.account.id, "USE");
    const message = await dependencies.repository.addHireMessage({
        id: dependencies.newId(),
        conversationId: parsed.conversationId,
        senderHorsemanId: actor.account.id,
        body: parsed.body,
        idempotencyKey: parsed.idempotencyKey,
        now: dependencies.clock.now(),
    });
    if (message === null) {
        throw new AppError({
            code: STABLE_ERROR_CODES.forbidden,
            message: "You cannot send a message in this conversation.",
        });
    }
    return hireMessageResponse(message, senderRole);
}
export async function listMyHireConversations(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const result = await dependencies.repository.listHireConversationsForApplicant({
        applicantHorsemanId: actor.account.id,
        cursor: input.cursor,
        limit: input.limit ?? 20,
    });
    return {
        conversations: result.conversations.map(hireConversationSummaryResponse),
        nextCursor: result.nextCursor,
    };
}
export async function listHireConversationsForStable(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listHireConversationsForStableSchema.parse(input);
    await requireFeatureMembership(dependencies, parsed.stableId, actor.account.id, FEATURE, "VIEW");
    const result = await dependencies.repository.listHireConversationsForStable({
        stableId: parsed.stableId,
        cursor: parsed.cursor,
        limit: parsed.limit,
    });
    return {
        conversations: result.conversations.map(hireConversationSummaryResponse),
        nextCursor: result.nextCursor,
    };
}
